import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";

export interface EventRoster {
  participantId: string;
  name: string;
  email: string;
  role: string | null;
  company: string | null;
  pitchStatus: string;
  hasTelegram: boolean;
  telegramUsername: string | null;
}

/**
 * Organizer-only roster for the "Notify participants" screen. Returns
 * every approved/submitted participant plus a flag for whether they're
 * reachable via the bot. Backed by `list_event_participants_for_organizer`
 * which double-checks ownership in the RPC.
 */
export async function fetchEventRosterForOrganizer(
  eventId: string,
): Promise<EventRoster[]> {
  const { data, error } = await supabase.rpc(
    "list_event_participants_for_organizer",
    { p_event_id: eventId },
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    participant_id: string;
    name: string;
    email: string;
    role: string | null;
    company: string | null;
    pitch_status: string;
    has_telegram: boolean;
    telegram_username: string | null;
  }>;
  return rows.map((r) => ({
    participantId: r.participant_id,
    name: r.name,
    email: r.email,
    role: r.role,
    company: r.company,
    pitchStatus: r.pitch_status,
    hasTelegram: r.has_telegram,
    telegramUsername: r.telegram_username,
  }));
}

export interface BroadcastResult {
  sent: number;
  failed: number;
  skipped: number;
}

/**
 * POST to the broadcast Edge Function. Throws on non-2xx so the caller
 * can show a useful error.
 */
export async function broadcastToEventTelegram(args: {
  eventId: string;
  participantIds: string[];
  message: string;
}): Promise<BroadcastResult> {
  const { data: sessionRes } = await supabase.auth.getSession();
  const jwt = sessionRes.session?.access_token;
  if (!jwt) throw new Error("Not signed in");

  const endpoint = `${env.VITE_SUPABASE_URL}/functions/v1/broadcast-event-telegram`;
  const resp = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({
      event_id: args.eventId,
      participant_ids: args.participantIds,
      message: args.message,
    }),
  });
  const data = (await resp.json().catch(() => ({}))) as {
    ok?: boolean;
    sent?: number;
    failed?: number;
    skipped?: number;
    error?: string;
  };
  if (!resp.ok || !data.ok) {
    throw new Error(data.error || `Broadcast failed (${resp.status})`);
  }
  return {
    sent: data.sent ?? 0,
    failed: data.failed ?? 0,
    skipped: data.skipped ?? 0,
  };
}
